import { ComponentPropsWithoutRef, FC, useState } from 'react';

import IconChevron from '@/assets/chevron.svg?react';
import { Question } from '@/types';
import cn from '@/utils/cn';

import Button from './Button';
import { i18n } from '@lingui/core';
import * as AccordionPrimitive from '@radix-ui/react-accordion';

interface Props extends ComponentPropsWithoutRef<'section'> {
  eyebrow?: string;
  title?: string;
  data: Question[];
}

const DEFAULT_DISPLAY_COUNT = 4;

const Accordion: FC<Props> = ({
  eyebrow = i18n.t('Rozwiewamy wątpliwości'),
  title = i18n.t('Najczęściej zadawane pytania'),
  className,
  data,
  ...props
}) => {
  const [open, setOpen] = useState('');
  const [displayCount, setDisplayCount] = useState(DEFAULT_DISPLAY_COUNT);

  const handleLoadMore = () => {
    if (displayCount === data.length) {
      setDisplayCount(4);
      return;
    }
    setDisplayCount(data.length);
  };

  return (
    <section
      className={cn('flex flex-col py-[42px] lg:p-10', className)}
      {...props}
    >
      <p className="input-label pb-3 text-blue-600">{eyebrow}</p>
      <h2 className="h2 text-dark-gray-500">{title}</h2>
      <AccordionPrimitive.Root
        value={open}
        type="single"
        onValueChange={(val: string) => {
          setOpen(val);
        }}
        collapsible
      >
        {data
          .slice(0, displayCount)
          .map(({ label, description, category }, key) => (
            <div key={label + key}>
              {/* headline only if prev category was de */}
              {category && category !== data[key - 1]?.category && (
                <h3 className="h3 pt-6">{category}</h3>
              )}
              <AccordionPrimitive.Item
                value={`${label}-${key}`}
                className="w-full cursor-pointer select-none "
              >
                <AccordionPrimitive.Trigger className="group w-full">
                  <div
                    className={cn(
                      'mt-6 flex items-center justify-between gap-4 text-left'
                    )}
                  >
                    <h5 className="h5 group-data-[state=closed]:text-oxford-blue-500 group-data-[state=open]:text-blue-600">
                      {label}
                    </h5>

                    <IconChevron
                      className="min-w-[14px] text-dark-gray-500 transition-transform duration-400 group-data-[state=closed]:rotate-90 group-data-[state=open]:rotate-[270deg] group-data-[state=closed]:text-oxford-blue-500 group-data-[state=open]:text-blue-600"
                      width={14}
                    />
                  </div>
                </AccordionPrimitive.Trigger>
                <AccordionPrimitive.Content className="max-w-[870px] overflow-hidden data-[state=closed]:animate-slideUp data-[state=open]:animate-slideDown">
                  <div className="body mt-4">{description}</div>
                </AccordionPrimitive.Content>
                <div className="mt-4 h-px w-full bg-gray-400" />
              </AccordionPrimitive.Item>
            </div>
          ))}
      </AccordionPrimitive.Root>
      <Button className="mt-[28px] px-4 py-2.5" onClick={handleLoadMore}>
        {displayCount < data.length ? 'Wczytaj więcej' : 'Ukryj część pytań'}
      </Button>
    </section>
  );
};

export default Accordion;
